import {
  colors,
  GetStoreProductChanges,
  SortOrder,
  StoreProductChange,
  StoreProductChangeEvents,
} from '@bofrak-backend/shared';
import { DatePicker, ProductHistoryTable } from '../../ui';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  Checkbox,
  CheckboxGroup,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  SimpleGrid,
  Skeleton,
  Spacer,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from '@chakra-ui/react';
import { endOfDay } from 'date-fns';
import { orderBy } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';
import { apiAdapter } from '../api/backend';
import Main from '../components/Main';
import { dateRangeAtom, merchantAtom, storeAtom } from '../recoil/atoms';
import { tShared, useSharedUiTranslation } from '../../i18n';

// Event configurations (tShared inside the function so labels follow the
// current language).
const getEventLabel = (event: StoreProductChangeEvents): string => {
  const labels: Record<StoreProductChangeEvents, string> = {
    [StoreProductChangeEvents.STORE_PRODUCT_SOLD_ONLINE_UPDATED]: tShared(
      'Sold Online Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_DISPLAY_COST_ON_POS_UPDATED]:
      tShared('Display Cost Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_COST_UPDATED]:
      tShared('Cost Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_PRICE_UPDATED]:
      tShared('Price Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_MIN_SELLING_PRICE_UPDATED]: tShared(
      'Min Selling Price Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_MAX_SELLING_PRICE_UPDATED]: tShared(
      'Max Selling Price Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_FRACTIONS_UPDATED]:
      tShared('Fractions Updated'),
    [StoreProductChangeEvents.STORE_PREFIX_UPDATED]: tShared('Prefix Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_DELETED]: tShared('Deleted'),
    [StoreProductChangeEvents.STORE_PRODUCT_LOYALTY_PROGRAMS_UPDATED]: tShared(
      'Loyalty Programs Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_MANUAL_CONTROL_UPDATED]: tShared(
      'Manual Control Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_IS_SOLD_BY_WEIGHT_UPDATED]: tShared(
      'Sold By Weight Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_IS_AVAILABLE_FOR_SALE_UPDATED]:
      tShared('Available For Sale Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_LOW_STOCK_THRESHOLD_UPDATED]:
      tShared('Low Stock Threshold Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_INVENTORY_UPDATED]:
      tShared('Inventory Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_SOLD_BY_WEIGHT_UPDATED]: tShared(
      'Sold By Weight Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_IS_AVAILABLE_IN_STORE_UPDATED]:
      tShared('Available In Store Updated'),
    [StoreProductChangeEvents.STORE_SKU_UPDATED]: tShared('SKU Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_CREATED]: tShared('Created'),
    [StoreProductChangeEvents.STORE_PRODUCT_IS_SOLD_BY_RANGE_UPDATED]: tShared(
      'Sold By Range Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_IS_SOLD_ONLINE_UPDATED]: tShared(
      'Sold Online Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_POPULARITY_SCORE_UPDATED]: tShared(
      'Popularity Score Updated',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_RESTORED]: tShared('Restored'),
    [StoreProductChangeEvents.STORE_PRODUCT_ALLOW_BELOW_COST_SALE_UPDATED]:
      tShared('Allow Below-Cost Sale Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_ASSIGNED]:
      tShared('Discount Assigned'),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_REASSIGNED]: tShared(
      'Discount Reassigned',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UNASSIGNED]: tShared(
      'Discount Unassigned',
    ),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UPDATED]:
      tShared('Discount Updated'),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_EXPIRED]:
      tShared('Discount Expired'),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_DELETED]:
      tShared('Discount Deleted'),
    [StoreProductChangeEvents.STORE_PRODUCT_DISCOUNTED_PRICE_UPDATED]: tShared(
      'Discounted Price Updated',
    ),
  };
  return labels[event] || event;
};

const DEFAULT_FILTERS = [
  StoreProductChangeEvents.STORE_PRODUCT_COST_UPDATED,
  StoreProductChangeEvents.STORE_PRODUCT_PRICE_UPDATED,
  StoreProductChangeEvents.STORE_PRODUCT_MAX_SELLING_PRICE_UPDATED,
  StoreProductChangeEvents.STORE_PRODUCT_MIN_SELLING_PRICE_UPDATED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_ASSIGNED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_REASSIGNED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UNASSIGNED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_UPDATED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_EXPIRED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNT_DELETED,
  StoreProductChangeEvents.STORE_PRODUCT_DISCOUNTED_PRICE_UPDATED,
];

interface EventSummary {
  event: StoreProductChangeEvents;
  count: number;
  label: string;
}

interface PriceChangeSummary {
  totalChanges: number;
  uniqueProducts: number;
  eventSummaries: EventSummary[];
  mostRecentChange?: StoreProductChange;
  earliestChange?: StoreProductChange;
}

const PriceHistory = () => {
  const { t } = useSharedUiTranslation();
  const store = useRecoilValue(storeAtom);
  const merchant = useRecoilValue(merchantAtom);

  const [selectedEvents, setSelectedEvents] =
    useState<StoreProductChangeEvents[]>(DEFAULT_FILTERS);
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);

  const isAllSelected = selectedEvents.length === DEFAULT_FILTERS.length;

  // Determine fetch command based on conditions
  const fetchCommand = useMemo(() => {
    if (isAllSelected && !dateRange.start) {
      return 'by_store_and_event';
    } else {
      return 'by_store_and_event_and_date_range';
    }
  }, [isAllSelected, dateRange.start]);

  // Fetch function
  const fetchPriceChanges = useCallback(
    async ({ pageParam }: any) => {
      if (!store?.id || !merchant?.id) {
        throw new Error('Store and merchant information required');
      }

      const baseDto: GetStoreProductChanges = {
        store_id: store.id,
        limit: 250,
        sort_order: SortOrder.DESC,
      };

      // Set the correct flag based on fetchCommand
      if (fetchCommand === 'by_store_and_event_and_date_range') {
        baseDto.by_store_and_event_and_date_range = true;
        if (dateRange.start) {
          baseDto.start_date = dateRange.start.toISOString();
          baseDto.end_date = dateRange.end?.toISOString();
        }
      } else {
        baseDto.by_store_and_event = true;
      }

      let allChanges: StoreProductChange[] = [];
      const nextPageParams: any = {};

      if (
        fetchCommand === 'by_store_and_event_and_date_range' &&
        selectedEvents.length > 0
      ) {
        // Fetch for each event individually
        const responses = await Promise.all(
          selectedEvents.map(async (event) => {
            const fetchDto: GetStoreProductChanges = {
              ...baseDto,
              event,
              cursor: pageParam ? pageParam[event as string] : undefined,
            };
            const response = await apiAdapter.getStoreProductChanges({
              storeProductChangeDto: fetchDto,
              merchant_id: merchant.id,
            });
            return { event, ...response };
          }),
        );

        // Combine changes and cursors
        for (const { event, changes, cursor } of responses) {
          allChanges = [...allChanges, ...changes];
          if (cursor) {
            nextPageParams[event] = cursor;
          }
        }
      } else {
        // Single fetch
        const fetchDto: GetStoreProductChanges = {
          ...baseDto,
          cursor: pageParam.cursor || undefined,
        };
        const response = await apiAdapter.getStoreProductChanges({
          storeProductChangeDto: fetchDto,
          merchant_id: merchant.id,
        });
        allChanges = response.changes;
        if (response.cursor) {
          nextPageParams.cursor = response.cursor;
        }
      }

      return {
        changes: allChanges,
        nextPageParams:
          Object.keys(nextPageParams).length > 0 ? nextPageParams : undefined,
      };
    },
    [store?.id, merchant?.id, selectedEvents, dateRange, fetchCommand],
  );

  // Infinite query
  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    [
      'inventoryChanges',
      store?.id,
      merchant?.id,
      dateRange,
      selectedEvents,
      fetchCommand,
    ],
    fetchPriceChanges,
    {
      getNextPageParam: (lastPage) => lastPage.nextPageParams,
      enabled: !!store?.id && !!merchant?.id && selectedEvents.length > 0,
    },
  );

  // Process data
  const allChanges = useMemo(() => {
    return data?.pages.flatMap((page) => page.changes) ?? [];
  }, [data]);

  const summary = useMemo((): PriceChangeSummary => {
    if (!allChanges.length) {
      return {
        totalChanges: 0,
        uniqueProducts: 0,
        eventSummaries: [],
      };
    }

    const uniqueProducts = new Set(allChanges.map((c) => c.id));
    const eventCounts = new Map<StoreProductChangeEvents, number>();

    allChanges.forEach((change) => {
      const count = eventCounts.get(change.event) || 0;
      eventCounts.set(change.event, count + 1);
    });

    const eventSummaries = Array.from(eventCounts.entries())
      .map(([event, count]) => ({
        event,
        count,
        label: getEventLabel(event),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalChanges: allChanges.length,
      uniqueProducts: uniqueProducts.size,
      eventSummaries,
      mostRecentChange: orderBy(allChanges, ['updated_at'], ['desc'])[0],
      earliestChange: orderBy(allChanges, ['updated_at'], ['asc'])[0],
    };
    // `t` in deps: event labels are translated inside the memo.
  }, [allChanges, t]);

  // Event handlers
  const handleEventChange = useCallback(
    (values: StoreProductChangeEvents[]) => {
      setSelectedEvents(values);
    },
    [],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedEvents(isAllSelected ? [] : DEFAULT_FILTERS);
  }, [isAllSelected]);

  const handleDateChange = useCallback((start: Date, end?: Date) => {
    setDateRange({
      start: start,
      end: end ? end : endOfDay(start),
    });
  }, []);

  const selectedEventsLabel = useMemo(() => {
    if (isAllSelected || selectedEvents.length === 0) {
      return t('All Price Events');
    }
    if (selectedEvents.length === 1) {
      return getEventLabel(selectedEvents[0]);
    }
    return t('{{count}} Events Selected', { count: selectedEvents.length });
  }, [selectedEvents, isAllSelected, t]);

  // Guard clauses
  if (!store || !merchant) {
    return (
      <Main>
        <Center h="60vh">
          <VStack spacing={2}>
            <Text color="gray.500">
              {t('Please select a store to view price history')}
            </Text>
          </VStack>
        </Center>
      </Main>
    );
  }

  if (error) {
    return (
      <Main>
        <Center h="60vh">
          <VStack spacing={4}>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {(error as Error).message}
            </Alert>
            <Button onClick={() => refetch()} colorScheme="blue">
              {t('Retry')}
            </Button>
          </VStack>
        </Center>
      </Main>
    );
  }

  return (
    <Main>
      <VStack spacing={6} w="full" align="stretch">
        {/* Header Card */}
        <Card>
          <CardHeader>
            <VStack spacing={4}>
              <Heading size="md">{t('Price History')}</Heading>

              <HStack w="full" spacing={3}>
                <DatePicker
                  isRange={true}
                  initialDate={dateRange.start ?? undefined}
                  initialEndDate={dateRange.end ?? undefined}
                  onDateChosen={handleDateChange}
                  fontSize={11}
                />

                <Spacer />

                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    colorScheme="blue"
                    size="sm"
                    minW="150px">
                    {selectedEventsLabel}
                  </MenuButton>
                  <MenuList maxW="300px" maxH="400px" overflowY="auto">
                    <MenuItem onClick={handleSelectAll}>
                      <Checkbox
                        isChecked={isAllSelected}
                        pointerEvents="none"
                        mr={2}
                      />
                      <Text>{t('Select All')}</Text>
                    </MenuItem>
                    <MenuDivider />
                    <CheckboxGroup
                      colorScheme="blue"
                      value={selectedEvents}
                      onChange={handleEventChange}>
                      <Stack spacing={1}>
                        {DEFAULT_FILTERS.map((filter) => (
                          <MenuItem key={filter} closeOnSelect={false}>
                            <Checkbox value={filter} w="full">
                              <Text fontSize="sm">{getEventLabel(filter)}</Text>
                            </Checkbox>
                          </MenuItem>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                  </MenuList>
                </Menu>
              </HStack>

              <Button
                w="full"
                isLoading={isFetching}
                borderRadius="full"
                onClick={() => refetch()}
                bg={colors.green}
                color="white">
                {t('Fetch')}
              </Button>
            </VStack>
          </CardHeader>
        </Card>

        {/* Summary Stats */}
        {summary.totalChanges > 0 && (
          <Card>
            <CardHeader pb={2}>
              <Heading size="sm">{t('Summary')}</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Stat>
                  <StatLabel fontSize="xs">{t('Total Changes')}</StatLabel>
                  <StatNumber fontSize="lg">{summary.totalChanges}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs">{t('Unique Products')}</StatLabel>
                  <StatNumber fontSize="lg">
                    {summary.uniqueProducts}
                  </StatNumber>
                </Stat>
                {summary.mostRecentChange && (
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('Most Recent Change')}
                    </StatLabel>
                    <StatNumber fontSize="md">
                      <Text>
                        {moment(summary.mostRecentChange?.updated_at).format(
                          'DD.MM.YYYY @ HH:mm',
                        )}
                      </Text>
                      <Text>
                        {getEventLabel(summary.mostRecentChange.event)}
                      </Text>
                    </StatNumber>
                  </Stat>
                )}
                {summary.earliestChange && (
                  <Stat>
                    <StatLabel fontSize="xs">{t('Earliest Change')}</StatLabel>
                    <StatNumber fontSize="md">
                      <Text>
                        {moment(summary.earliestChange?.updated_at).format(
                          'DD.MM.YYYY @ HH:mm',
                        )}
                      </Text>
                      <Text>{getEventLabel(summary.earliestChange.event)}</Text>
                    </StatNumber>
                  </Stat>
                )}
              </SimpleGrid>

              {summary.eventSummaries.length > 2 && (
                <Box mt={4}>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    {t('Event Breakdown')}
                  </Text>
                  <HStack wrap="wrap" spacing={2}>
                    {summary.eventSummaries.map(({ event, count }) => (
                      <Badge key={event} colorScheme="blue" px={2} py={1}>
                        {getEventLabel(event)}: {count}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}
            </CardBody>
          </Card>
        )}

        {/* Changes Table */}
        <Card>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="sm">{t('Change History')}</Heading>
              {allChanges.length > 0 && (
                <Text fontSize="sm" color="gray.500">
                  {t('{{count}} change', { count: allChanges.length })}
                </Text>
              )}
            </HStack>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <VStack spacing={3}>
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} height="60px" w="full" />
                ))}
              </VStack>
            ) : allChanges.length === 0 ? (
              <Center py={8}>
                <VStack spacing={2}>
                  <Text color="gray.500">
                    {selectedEvents.length === 0
                      ? t('Please select at least one event type')
                      : t('No price changes found for the selected criteria')}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <VStack spacing={4}>
                <ProductHistoryTable data={allChanges} />

                {hasNextPage && (
                  <Center pt={4}>
                    <Button
                      onClick={() => fetchNextPage()}
                      isLoading={isFetchingNextPage}
                      loadingText={t('Loading more...')}
                      colorScheme="blue"
                      variant="outline"
                      size="sm">
                      {t('Load More Changes')}
                    </Button>
                  </Center>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>
      </VStack>
    </Main>
  );
};

export default PriceHistory;
